import React from 'react';
import {
    ComponentTextSmall,
    HeadingText20,
    SkeletonPlaceholder,
} from '@modernhealth/vital-components';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { useLanguageCode } from '../hooks/useLanguageCode';

import { CircleOnDemandDuration } from './CircleOnDemandDuration';
import { CircleOnDemandLanguage } from './CircleOnDemandLanguage';
import { CircleCardHeaderLink } from './CircleCardHeaderLink';

export const CircleOnDemandCardSkeleton = () => {
    return <SkeletonPlaceholder width="100%" height={288} />;
};

export const CircleOnDemandCard: React.FC<{
    slug: string;
    name: string;
    thumb: string;
    seconds: number;
    languageCode?: string;
    host?: string;
}> = ({ slug, name, thumb, seconds, host, languageCode }) => {
    const { t } = useTranslation('circles');
    const userLanguageCode = useLanguageCode();

    const isDifferentLanguage = languageCode && languageCode !== userLanguageCode;

    return (
        <Container>
            <div
                css={{
                    position: 'relative',
                    marginBottom: 16,
                }}
            >
                {languageCode && isDifferentLanguage ? (
                    <CircleOnDemandLanguage languageCode={languageCode} />
                ) : null}
                <Thumbnail src={thumb} alt="" />
                <CircleOnDemandDuration seconds={seconds} />
            </div>

            <HeadingText20 css={{ marginBottom: 10 }}>
                <CircleCardHeaderLink
                    to={`/circles/on-demand/${slug}`}
                    name={name}
                    aria-describedby={`circle-ondemand-topic-${slug}`}
                />
            </HeadingText20>
            {host ? (
                <ComponentTextSmall fontColor="gray65">
                    {t('cards.on_demand.by_host', { host })}
                </ComponentTextSmall>
            ) : null}
        </Container>
    );
};

const Thumbnail = styled.img({
    borderRadius: 6,
    width: '100%',
    maxHeight: 228,
    objectFit: 'cover',
});

const Container = styled.li({
    display: 'inline-flex',
    flexDirection: 'column',
    position: 'relative',
    flex: '1 1 0%',
    width: '100%',
    minWidth: 300,
});
